/**
 * Input Method Context & Provider
 * Manages global input method state, element registry, and UI adaptations
 * Single source of truth for all input interactions
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { 
  InputMethod, 
  InputMethodType, 
  VoiceState, 
  VoiceStateType,
  resolveInputBehavior,
  controlMethodToInputMethod,
  INPUT_GROUPS,
  SCAN_MODES,
  InputBehavior
} from '@/lib/inputMethods';
import { getUserSettings } from '@/lib/userSettings';
import { getScoped, setScoped } from '@/lib/userScopedStorage';

// Element registration types
interface RegisteredElement {
  id: string;
  ref?: React.RefObject<HTMLElement>;
  onClick?: () => void;
  label?: string;
  group?: string;
  order: number;
  registeredAt: number;
}

// Scanning configuration
interface ScanningConfig {
  pattern: string;
  speed: number;
  skipEmpty: boolean;
  inputCount: number;
}

// Context value type
interface InputMethodContextValue {
  // Core
  inputMethod: InputMethodType;
  setInputMethod: (method: InputMethodType) => void;
  inputGroup: string;
  
  // Scanning config
  scanningConfig: ScanningConfig;
  setScanningConfig: (config: Partial<ScanningConfig> | ((prev: ScanningConfig) => ScanningConfig)) => void;
  
  // Voice
  voiceState: VoiceStateType;
  setVoiceState: (state: VoiceStateType) => void;
  wakeWordDetected: boolean;
  handleWakeWord: () => void;
  executeVoiceCommand: (command: string) => boolean;
  lastCommand: string | null;
  voiceEnabled: boolean;
  
  // Tracking
  isTrackingActive: boolean;
  pauseTracking: () => void;
  resumeTracking: () => void;
  cursorPosition: { x: number; y: number };
  updateCursorPosition: (x: number, y: number) => void;
  dwellProgress: number;
  setDwellProgress: (progress: number) => void;
  dwellTarget: string | null;
  setDwellTarget: (target: string | null) => void;
  isDwellEnabled: boolean;
  
  // Switch control
  focusedElementId: string | null;
  setFocusedElementId: (id: string | null) => void;
  navigateFocus: (direction: 'up' | 'down' | 'left' | 'right' | 'enter') => void;
  
  // Input behavior
  inputBehavior: InputBehavior;
  
  // UI flags
  showFocusRing: boolean;
  showNumberTags: boolean;
  showVirtualCursor: boolean;
  showDwellIndicator: boolean;
  hideCursor: boolean;
  scanMode: string;
  
  // Registry
  registerElement: (id: string, elementInfo: Partial<RegisteredElement>) => () => void;
  unregisterElement: (id: string) => void;
  registeredElements: RegisteredElement[];
  getElementByNumber: (number: number) => RegisteredElement | null;
  getElementById: (id: string) => RegisteredElement | null;
}

// Default context value
const defaultContext: InputMethodContextValue = {
  inputMethod: InputMethod.STANDARD_POINTER,
  setInputMethod: () => {},
  inputGroup: INPUT_GROUPS.POINTER,
  
  scanningConfig: { pattern: 'LINEAR', speed: 3000, skipEmpty: true, inputCount: 1 },
  setScanningConfig: () => {},
  
  voiceState: VoiceState.SLEEPING,
  setVoiceState: () => {},
  wakeWordDetected: false,
  handleWakeWord: () => {},
  executeVoiceCommand: () => false,
  lastCommand: null,
  voiceEnabled: false,
  
  isTrackingActive: true,
  pauseTracking: () => {},
  resumeTracking: () => {},
  cursorPosition: { x: 0, y: 0 },
  updateCursorPosition: () => {},
  dwellProgress: 0,
  setDwellProgress: () => {},
  dwellTarget: null,
  setDwellTarget: () => {},
  isDwellEnabled: false,
  
  focusedElementId: null,
  setFocusedElementId: () => {},
  navigateFocus: () => {},
  
  inputBehavior: resolveInputBehavior(InputMethod.STANDARD_POINTER, 1),
  
  showFocusRing: false,
  showNumberTags: false,
  showVirtualCursor: false,
  showDwellIndicator: false,
  hideCursor: false,
  scanMode: SCAN_MODES.NONE,
  
  registerElement: () => () => {},
  unregisterElement: () => {},
  registeredElements: [],
  getElementByNumber: () => null,
  getElementById: () => null,
};

const InputMethodContext = createContext<InputMethodContextValue>(defaultContext);

export function useInputMethod() {
  return useContext(InputMethodContext);
}

// Storage keys
const STORAGE_KEY = 'ayuni_input_method';
const SCANNING_CONFIG_KEY = 'ayuni_scanning_config';

interface InputMethodProviderProps {
  children: ReactNode;
  initialMethod?: InputMethodType;
}

export function InputMethodProvider({ children, initialMethod = InputMethod.STANDARD_POINTER }: InputMethodProviderProps) {
  // Core state - Initialize synchronously from localStorage/sessionStorage/userSettings
  const [inputMethod, setInputMethodState] = useState<InputMethodType>(() => {
    if (typeof window === 'undefined') return initialMethod;
    
    // Check userSettings first (most authoritative)
    const userSettings = getUserSettings();
    if (userSettings.controlMethod) {
      const mapped = controlMethodToInputMethod[userSettings.controlMethod];
      if (mapped) return mapped;
    }
    
    // Check sessionStorage second
    const sessionMethod = sessionStorage.getItem('controlMethod');
    if (sessionMethod) {
      const mapped = controlMethodToInputMethod[sessionMethod];
      if (mapped) return mapped;
    }
    
    // Fallback to localStorage
    const stored = getScoped(STORAGE_KEY);
    if (stored && Object.values(InputMethod).includes(stored as InputMethodType)) {
      return stored as InputMethodType;
    }
    return initialMethod;
  });
  
  // Scanning configuration
  const [scanningConfig, setScanningConfigState] = useState<ScanningConfig>(() => {
    if (typeof window === 'undefined') return { pattern: 'LINEAR', speed: 3000, skipEmpty: true, inputCount: 1 };
    try {
      const stored = getScoped(SCANNING_CONFIG_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {}
    return { pattern: 'LINEAR', speed: 3000, skipEmpty: true, inputCount: 1 };
  });

  // Wrapped setter that persists to localStorage and resets relevant state
  const setInputMethod = useCallback((method: InputMethodType) => {
    setInputMethodState(prev => {
      // Reset tracking state when switching methods
      if (prev !== method) {
        setIsTrackingActive(true);
        setDwellProgress(0);
        setDwellTarget(null);
        setCursorPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
      }
      return method;
    });
    if (typeof window !== 'undefined') {
      setScoped(STORAGE_KEY, method);
      sessionStorage.setItem('controlMethod', method);
      window.dispatchEvent(new CustomEvent('inputMethodChanged', { detail: { method } }));
    }
  }, []);

  // Wrapped setter for scanning config
  const setScanningConfig = useCallback((config: Partial<ScanningConfig> | ((prev: ScanningConfig) => ScanningConfig)) => {
    setScanningConfigState(prev => {
      const newConfig = typeof config === 'function' ? config(prev) : { ...prev, ...config };
      if (typeof window !== 'undefined') {
        setScoped(SCANNING_CONFIG_KEY, JSON.stringify(newConfig));
        window.dispatchEvent(new CustomEvent('scanningConfigChanged', { detail: newConfig }));
      }
      return newConfig;
    });
  }, []);
  
  // Voice control state
  const [voiceState, setVoiceState] = useState<VoiceStateType>(VoiceState.SLEEPING);
  const [wakeWordDetected, setWakeWordDetected] = useState(false);
  const [lastCommand, setLastCommand] = useState<string | null>(null);
  const voiceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Gaze/Head tracking state
  const [isTrackingActive, setIsTrackingActive] = useState(true);
  const [cursorPosition, setCursorPosition] = useState({ x: typeof window !== 'undefined' ? window.innerWidth / 2 : 500, y: typeof window !== 'undefined' ? window.innerHeight / 2 : 400 });
  const [dwellProgress, setDwellProgress] = useState(0);
  const [dwellTarget, setDwellTarget] = useState<string | null>(null);
  
  // Switch control state
  const [focusedElementId, setFocusedElementId] = useState<string | null>(null);
  
  // Element registry
  const [registeredElements, setRegisteredElements] = useState<RegisteredElement[]>([]);
  const elementRegistry = useRef(new Map<string, RegisteredElement>());
  const elementOrderCounter = useRef(0);

  // Get switch/input count - from user settings for external switch, otherwise from scanning config
  const getInputCount = useCallback(() => {
    if (inputMethod === InputMethod.EXTERNAL_SWITCH) {
      const settings = getUserSettings();
      return settings.externalSwitchCount || 1;
    }
    if (inputMethod === InputMethod.EYE_CONTROL) {
      const settings = getUserSettings();
      return settings.eyeControlGestures?.length || 1;
    }
    if (inputMethod === InputMethod.MOUTH_GESTURES) {
      const settings = getUserSettings();
      return settings.mouthGestures?.length || 1;
    }
    return scanningConfig?.inputCount || 1;
  }, [inputMethod, scanningConfig?.inputCount]);

  const inputCount = getInputCount();
  
  // Derive UI flags from input method
  const inputBehavior = resolveInputBehavior(inputMethod, inputCount);
  
  const uiFlags = {
    showFocusRing: inputBehavior.showFocusRing,
    showNumberTags: inputBehavior.showNumberTags,
    showVirtualCursor: inputBehavior.showVirtualCursor,
    showDwellIndicator: inputBehavior.dwell,
    hideCursor: inputBehavior.hideCursor,
    scanMode: inputBehavior.scanning
  };

  /**
   * Register an interactive element
   */
  const registerElement = useCallback((id: string, elementInfo: Partial<RegisteredElement>): () => void => {
    const elementId = id || `element-${++elementOrderCounter.current}`;
    const order = elementOrderCounter.current;
    
    const newElement: RegisteredElement = { 
      ...elementInfo as RegisteredElement,
      id: elementId,
      order,
      registeredAt: Date.now() 
    };
    
    elementRegistry.current.set(elementId, newElement);
    
    // Update state with sorted elements
    const sortedElements = Array.from(elementRegistry.current.values())
      .sort((a, b) => a.order - b.order);
    
    setRegisteredElements(sortedElements);
    
    // Return unregister function
    return () => unregisterElement(elementId);
  }, []);

  /**
   * Unregister an element
   */
  const unregisterElement = useCallback((id: string) => {
    elementRegistry.current.delete(id);
    
    const sortedElements = Array.from(elementRegistry.current.values())
      .sort((a, b) => a.order - b.order);
    
    setRegisteredElements(sortedElements);
  }, []);

  /**
   * Get element by voice number (1-indexed)
   */
  const getElementByNumber = useCallback((number: number): RegisteredElement | null => {
    const elements = Array.from(elementRegistry.current.values())
      .sort((a, b) => a.order - b.order);
    
    if (number > 0 && number <= elements.length) {
      return elements[number - 1];
    }
    return null;
  }, []);

  /**
   * Get element by ID
   */
  const getElementById = useCallback((id: string): RegisteredElement | null => {
    return elementRegistry.current.get(id) || null;
  }, []);

  /**
   * Voice: Handle wake word detection
   */
  const handleWakeWord = useCallback(() => {
    if (inputMethod !== InputMethod.VOICE_CONTROL) return;
    
    setWakeWordDetected(true);
    setVoiceState(VoiceState.LISTENING);
    
    // Auto-timeout after 5 seconds
    if (voiceTimeoutRef.current) {
      clearTimeout(voiceTimeoutRef.current);
    }
    voiceTimeoutRef.current = setTimeout(() => {
      setWakeWordDetected(false);
      setVoiceState(VoiceState.STANDBY);
    }, 5000);
  }, [inputMethod]);

  /**
   * Voice: Execute a voice command
   */
  const executeVoiceCommand = useCallback((command: string): boolean => {
    if (!wakeWordDetected || voiceState !== VoiceState.LISTENING) return false;
    
    setLastCommand(command);
    setVoiceState(VoiceState.PROCESSING);
    
    let targetElement: RegisteredElement | null = null;
    
    // Try number first
    const numberMatch = command.match(/\d+/);
    if (numberMatch) {
      const number = parseInt(numberMatch[0]);
      targetElement = getElementByNumber(number);
    }
    
    // Try label match
    if (!targetElement) {
      for (const element of registeredElements) {
        if (element.label && element.label.toLowerCase().includes(command.toLowerCase())) {
          targetElement = element;
          break;
        }
      }
    }
    
    // Execute click on target
    if (targetElement && targetElement.onClick) {
      targetElement.onClick();
    }
    
    // Return to standby
    setTimeout(() => {
      setWakeWordDetected(false);
      setVoiceState(VoiceState.STANDBY);
    }, 500);
    
    return !!targetElement;
  }, [wakeWordDetected, voiceState, getElementByNumber, registeredElements]);

  // Gaze/Head tracking: Pause/Resume
  const pauseTracking = useCallback(() => {
    setIsTrackingActive(false);
    setDwellProgress(0);
    setDwellTarget(null);
  }, []);

  const resumeTracking = useCallback(() => {
    setIsTrackingActive(true);
  }, []);

  // Update cursor position
  const updateCursorPosition = useCallback((x: number, y: number) => {
    if (!isTrackingActive) return;
    setCursorPosition({ x, y });
  }, [isTrackingActive]);

  // Switch control: Navigate focus
  const navigateFocus = useCallback((direction: 'up' | 'down' | 'left' | 'right' | 'enter') => {
    if (inputMethod !== InputMethod.EXTERNAL_SWITCH && inputMethod !== InputMethod.MOUTH_GESTURES && inputMethod !== InputMethod.EYE_CONTROL) return;
    
    if (registeredElements.length === 0) return;
    
    const currentIndex = registeredElements.findIndex(el => el.id === focusedElementId);
    let newIndex = currentIndex;
    
    switch (direction) {
      case 'up':
      case 'left':
        newIndex = currentIndex > 0 ? currentIndex - 1 : registeredElements.length - 1;
        break;
      case 'down':
      case 'right':
        newIndex = currentIndex < registeredElements.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'enter':
        if (currentIndex >= 0) {
          const element = registeredElements[currentIndex];
          element.onClick?.();
          // Also try clicking the DOM ref
          if (element.ref?.current) {
            element.ref.current.click();
          }
        }
        return;
    }
    
    const newElement = registeredElements[newIndex];
    setFocusedElementId(newElement.id);
    
    // Scroll into view
    if (newElement.ref?.current) {
      newElement.ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [inputMethod, focusedElementId, registeredElements]);

  // Listen for controlMethodChanged and inputMethodChanged events
  useEffect(() => {
    const handleControlMethodChanged = (e: CustomEvent<{ method: string }>) => {
      const sessionMethod = e.detail?.method || sessionStorage.getItem('controlMethod');
      if (sessionMethod) {
        const mapped = controlMethodToInputMethod[sessionMethod];
        if (mapped && mapped !== inputMethod) {
          // Reset tracking state
          setIsTrackingActive(true);
          setDwellProgress(0);
          setDwellTarget(null);
          setCursorPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
          
          setInputMethodState(mapped);
          if (typeof window !== 'undefined') {
            setScoped(STORAGE_KEY, mapped);
          }
          // Also reload scanning config
          try {
            const storedConfig = getScoped(SCANNING_CONFIG_KEY);
            if (storedConfig) {
              setScanningConfigState(JSON.parse(storedConfig));
            }
          } catch {}
        }
      }
    };
    
    window.addEventListener('controlMethodChanged', handleControlMethodChanged as EventListener);
    window.addEventListener('inputMethodChanged', handleControlMethodChanged as EventListener);
    return () => {
      window.removeEventListener('controlMethodChanged', handleControlMethodChanged as EventListener);
      window.removeEventListener('inputMethodChanged', handleControlMethodChanged as EventListener);
    };
  }, [inputMethod]);

  // Listen for scanningConfigChanged event
  useEffect(() => {
    const handleScanningConfigChanged = () => {
      try {
        const stored = getScoped(SCANNING_CONFIG_KEY);
        if (stored) {
          const newConfig = JSON.parse(stored);
          setScanningConfigState(newConfig);
        }
      } catch {
        // Silently fail
      }
    };
    
    window.addEventListener('scanningConfigChanged', handleScanningConfigChanged);
    return () => window.removeEventListener('scanningConfigChanged', handleScanningConfigChanged);
  }, []);

  // Initialize voice recognition when in voice mode
  useEffect(() => {
    if (inputMethod === InputMethod.VOICE_CONTROL) {
      setVoiceState(VoiceState.STANDBY);
    } else {
      setVoiceState(VoiceState.SLEEPING);
      setWakeWordDetected(false);
    }
    
    return () => {
      if (voiceTimeoutRef.current) {
        clearTimeout(voiceTimeoutRef.current);
      }
    };
  }, [inputMethod]);

  // System cursor should always remain visible
  useEffect(() => {
    document.body.style.cursor = 'auto';
    document.body.classList.remove('hide-cursor');
    document.documentElement.style.cursor = '';
  }, [inputMethod]);

  // Derive inputGroup
  const inputGroup = inputBehavior.group;

  const contextValue: InputMethodContextValue = {
    inputMethod,
    setInputMethod,
    inputGroup,
    
    scanningConfig,
    setScanningConfig,
    
    voiceState,
    setVoiceState,
    wakeWordDetected,
    handleWakeWord,
    executeVoiceCommand,
    lastCommand,
    voiceEnabled: inputMethod === InputMethod.VOICE_CONTROL,
    
    isTrackingActive,
    pauseTracking,
    resumeTracking,
    cursorPosition,
    updateCursorPosition,
    dwellProgress,
    setDwellProgress,
    dwellTarget,
    setDwellTarget,
    isDwellEnabled: inputMethod === InputMethod.GAZE_TRACKING || inputMethod === InputMethod.HEAD_TRACKING,
    
    focusedElementId,
    setFocusedElementId,
    navigateFocus,
    
    inputBehavior,
    ...uiFlags,
    
    registerElement,
    unregisterElement,
    registeredElements,
    getElementByNumber,
    getElementById,
  };

  return (
    <InputMethodContext.Provider value={contextValue}>
      {children}
    </InputMethodContext.Provider>
  );
}

export default InputMethodContext;
